import { HTMLAttributes } from 'react'
import Link from 'next/link'

import Grid from '@/components/base/grid'
import Media, { MediaElement } from '@/components/base/media'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'

import { mediaTextAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPLink } from '@/types'

export type MediaTextProps = HTMLAttributes<HTMLDivElement> &
  Partial<{ paragraph: string; media: MediaElement; link: WPLink }>

const MediaText = ({
  paragraph,
  media,
  link,
  className,
  ...props
}: MediaTextProps) => {
  return (
    <Motion animation={mediaTextAnimation}>
      {(motionRef) => (
        <Section
          ref={motionRef}
          className={cn('px-margin lg:px-0', className)}
          {...props}
        >
          <Grid>
            <div
              className={cn(
                'col-span-2 md:col-span-4 md:col-start-2',
                'lg:col-auto lg:row-span-8 lg:row-start-3 lg:w-col-span-6',
                'grid grid-cols-6 items-end gap-x-gutter'
              )}
            >
              <div
                className={cn(
                  'relative col-span-3 aspect-[288/350]',
                  'after:absolute after:left-0 after:top-0 after:h-full after:w-full after:bg-black/45',
                  'mb-[50%] md:mb-0'
                )}
                data-motion-media
              >
                <Media fill fit="cover" {...media} />
              </div>

              <div
                className={cn(
                  'relative col-span-3 flex flex-col space-y-[3rem]',
                  '-ml-[4.5rem] md:ml-0'
                )}
                data-motion-text
              >
                {paragraph ? (
                  <Typography
                    paragraph
                    className="text-[1.1rem] font-bold uppercase leading-[1.4rem] tracking-[0.065rem] md:text-[1.3rem]"
                  >
                    {paragraph}
                  </Typography>
                ) : null}

                {link ? (
                  <Link
                    scroll={false}
                    href={link.url}
                    target={link.target}
                    className="link flex"
                  >
                    <Typography className="text-[1.1rem] font-bold uppercase leading-[1.4rem] tracking-[0.05rem] md:text-[1.3rem]">
                      {link.title}
                    </Typography>
                  </Link>
                ) : null}
              </div>
            </div>
          </Grid>
        </Section>
      )}
    </Motion>
  )
}

export default MediaText
